import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  addBankAccount,
  fetchBankAccounts,
  updateBankAccount,
  deleteBankAccount,
  resetStatus
} from '../redux/slices/bankAccountSlice';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../assets/CompanyBankAccount.css';

const CompanyBankAccount = () => {
  const dispatch = useDispatch();
  const { loading, success, error, accounts = [], totalPages } = useSelector(state => state.bankAccount);

  const [formData, setFormData] = useState({ accountNumber: '', accountName: '', ifscCode: '', bankName: '', branch: '', micrCode: '' });
  const [errors, setErrors] = useState({});
  const [editIndex, setEditIndex] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const superAdminId = localStorage.getItem('adminuserId');

  useEffect(() => {
  dispatch(fetchBankAccounts({
    page,
    superAdminId
  }));
}, [dispatch, page, superAdminId]);

  useEffect(() => {
    if (success) {
      toast.success(editingId ? 'Bank account updated successfully' : 'Bank account added successfully');
      dispatch(fetchBankAccounts(page));
      dispatch(resetStatus());
    }
  }, [success, dispatch, editingId, page]);

  useEffect(() => {
    if (error) toast.error(`Error: ${error}`);
  }, [error]);

  useEffect(() => {
  dispatch(fetchBankAccounts(page));
}, [dispatch, page]);

useEffect(() => {
  console.log("Accounts:", accounts);
}, [accounts]);

  const validateField = (name, value) => {
    let error = "";

    if (!value.trim()) {
      error = `${name} is required`;
    } else {
      if (name === "accountNumber") {
        if (!/^\d+$/.test(value)) error = "Account Number must be digits only";
        else if (value.length > 16) error = "Account Number cannot exceed 16 digits";
      }
      if (name === "ifscCode") {
        if (!/^[A-Za-z]{4}[a-zA-Z0-9]{7}$/.test(value))
          error = "IFSC must be 11 characters (e.g. SBIN0001234)";
      }
      if (name === "micrCode") {
        if (!/^\d{9}$/.test(value)) error = "MICR must be exactly 9 digits";
      }
    }
    return error;
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleSubmit = e => {
    e.preventDefault();
    let newErrors = {};
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const payload = {...formData, status: 'Active', superAdminId};
    if (editingId !== null) {
      dispatch(updateBankAccount({ id: editingId, updatedData: payload }));
    } else {
      dispatch(addBankAccount(payload));
    }

    setFormData({ accountNumber: '', accountName: '', ifscCode: '', bankName: '', branch: '', micrCode: '' });
    setEditIndex(null);
    setEditingId(null);
    setErrors({});
  };

  const handleEdit = index => {
    const data = filteredAccounts[index];
    setFormData({
      accountNumber: data.accountNumber,
      accountName: data.accountName,
      ifscCode: data.ifscCode,
      bankName: data.bankName,
      branch: data.branch,
      micrCode: data.micrCode
    });
    setEditIndex(index);
    setEditingId(data.id);
  };

  const handleDelete = index => {
    const account = filteredAccounts[index];
    if (window.confirm('Are you sure you want to delete this account?')) {
      dispatch(deleteBankAccount(account.id));
      toast.success('Bank account deleted successfully');
    }
  };

  const handleSort = key => {
    const order = key === sortKey && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortKey(key);
    setSortOrder(order);
  };

  const filteredAccounts = accounts
    .filter(acc =>
      Object.values(acc).some(val =>
        val?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    )
    .sort((a, b) => {
      if (!sortKey) return 0;
      const x = a[sortKey]?.toString().toLowerCase() || '';
      const y = b[sortKey]?.toString().toLowerCase() || '';
      return sortOrder === 'asc' ? x.localeCompare(y) : y.localeCompare(x);
    });

  return (
    <div className="CompanyBankAccount-container">
      <h2 className="CompanyBankAccount-title">Company Bank Account Master</h2>

      <div className="CompanyBankAccount-formSection">
        <h3>{editingId ? 'Edit' : 'Add'} Bank Account</h3>
        <form onSubmit={handleSubmit} className="CompanyBankAccount-form">
          {[
            { name: "accountNumber", label: "Account Number" },
            { name: "accountName", label: "Account Name" },
            { name: "ifscCode", label: "IFSC Code" },
            { name: "bankName", label: "Bank Name" },
            { name: "branch", label: "Branch Name" },
            { name: "micrCode", label: "MICR Code" }
          ].map(({ name, label }) => (
            <div key={name} className="form-group">
              <label>
                {label} <span style={{ color: "red" }}>*</span>
              </label>
              <input
                name={name}
                value={formData[name]}
                onChange={handleChange}
                placeholder={label}
                required
              />
              {errors[name] && <small style={{ color: "red" }}>{errors[name]}</small>}
            </div>
          ))}
          <button type="submit" disabled={loading}>
            {editingId ? 'Update' : loading ? 'Saving...' : 'Save'}
          </button>
        </form>
      </div>

      <div className="CompanyBankAccount-tableSection">
        <div className="search-wrapper">
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="CompanyBankAccount-tableWrapper">
          <table className="CompanyBankAccount-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('id')}>S.No</th>
                <th onClick={() => handleSort('accountNumber')}>Account Number</th>
                <th onClick={() => handleSort('accountName')}>Account Name</th>
                <th onClick={() => handleSort('bankName')}>Bank Name</th>
                <th onClick={() => handleSort('branch')}>Branch</th>
                <th onClick={() => handleSort('ifscCode')}>IFSC Code</th>
                <th onClick={() => handleSort('micrCode')}>MICR Code</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAccounts.length > 0 ? (
                filteredAccounts.map((acc, index) => (
                  <tr key={acc.id}>
                    <td className="blue-cell">{index + 1}</td>
                    <td className="blue-cell">{acc.accountNumber||"N/A"}</td>
                    <td className="blue-cell">{acc.accountName||"N/A"}</td>
                    <td className="blue-cell">{acc.bankName||"N/A"}</td>
                    <td className="blue-cell">{acc.branch||"N/A"}</td>
                    <td className="blue-cell">{acc.ifscCode||"N/A"}</td>
                    <td className="blue-cell">{acc.micrCode||"N/A"}</td>
                    <td>
                      <button onClick={() => handleEdit(index)}><i className="fas fa-edit"></i> Edit</button>
                      <button onClick={() => handleDelete(index)}><i className="fas fa-trash"></i> Delete</button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="8" className="CompanyBankAccount-empty">No Data Available</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          {[...Array(totalPages)].map((_, i) => (
            <button key={i} className={page === i + 1 ? 'active' : ''} onClick={() => setPage(i + 1)}>{i + 1}</button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CompanyBankAccount;
